import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, Heart, User, ChevronDown, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useFavoritesContext } from '@/context/FavoritesContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites } = useFavoritesContext();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Handle search submit
  const handleSearch = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  }, [searchQuery, navigate]);

  const navLinks = [
    { href: '/', label: 'Inicio', hasDropdown: false },
    {
      href: '/movies',
      label: 'Películas',
      hasDropdown: true,
      items: [
        { label: 'Popular', href: '/movies?sort=popularity' },
        { label: 'Mejor Valoradas', href: '/movies?sort=top_rated' },
        { label: 'Próximamente', href: '/movies?sort=upcoming' },
      ]
    },
    {
      href: '/series',
      label: 'Series',
      hasDropdown: true,
      items: [
        { label: 'Tendencia', href: '/series?sort=popular' },
        { label: 'Mejor Valoradas', href: '/series?sort=top_rated' },
      ]
    },
    {
      href: '/genres',
      label: 'Géneros',
      hasDropdown: true,
      items: [
        { label: 'Acción', href: '/genre/28' },
        { label: 'Aventura', href: '/genre/12' },
        { label: 'Comedia', href: '/genre/35' },
        { label: 'Drama', href: '/genre/18' },
        { label: 'Terror', href: '/genre/27' },
        { label: 'Ciencia Ficción', href: '/genre/878' },
        { label: 'Fantasía', href: '/genre/14' },
      ]
    },
  ];

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
        isScrolled
          ? 'bg-[#0f1014]/90 backdrop-blur-xl border-white/5 py-2'
          : 'bg-transparent border-transparent py-4'
      )}
    >
      <nav className="max-w-[1800px] mx-auto px-4 md:px-8 flex items-center justify-between h-16">

        {/* Left: Brand & Nav */}
        <div className="flex items-center gap-8 lg:gap-12">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group z-50">
            <img
              src="/logo.png"
              alt="Cinemax"
              className="h-14 md:h-16 w-auto object-contain group-hover:scale-105 transition-transform duration-300 drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <div
                key={link.href}
                className="relative group"
                onMouseEnter={() => setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.href}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300',
                    activeDropdown === link.label || location.pathname === link.href
                      ? 'text-white bg-white/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  )}
                >
                  {link.label}
                  {link.hasDropdown && (
                    <ChevronDown
                      className={cn(
                        "w-3.5 h-3.5 transition-transform duration-300",
                        activeDropdown === link.label ? "rotate-180" : "rotate-0 text-gray-500"
                      )}
                      strokeWidth={2.5}
                    />
                  )}
                </Link>

                {/* Dropdown Menu */}
                {link.hasDropdown && (
                  <div
                    className={cn(
                      "absolute top-full left-0 pt-2 w-56 transition-all duration-200 origin-top-left",
                      activeDropdown === link.label
                        ? "opacity-100 translate-y-0 visible"
                        : "opacity-0 translate-y-1 invisible"
                    )}
                  >
                    <div className="bg-[#15171e] border border-white/10 rounded-xl shadow-2xl overflow-hidden backdrop-blur-3xl ring-1 ring-black/20">
                      <div className="p-1.5 flex flex-col gap-0.5">
                        {link.items?.map((item, idx) => (
                          <Link
                            key={idx}
                            to={item.href}
                            className="px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors flex items-center justify-between group/item"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right: Search & User */}
        <div className="flex items-center gap-3 sm:gap-6">
          {/* Search Input (Pill) */}
          <div className="hidden md:block relative group">
            <form onSubmit={handleSearch}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar películas..."
                className="w-64 bg-[#1a1d24] border border-white/10 text-gray-200 text-sm rounded-full py-2.5 pl-5 pr-12 focus:outline-none focus:border-blue-500/50 focus:bg-[#20242c] focus:ring-1 focus:ring-blue-500/20 transition-all placeholder:text-gray-500"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 bg-blue-600 hover:bg-blue-500 rounded-full text-white transition-all shadow-lg shadow-blue-500/20"
              >
                <Search className="w-3.5 h-3.5" strokeWidth={3} />
              </button>
            </form>
          </div>

          {/* Icons */}
          <div className="flex items-center gap-3">
            {/* Favorites */}
            <Link to="/favorites" className="relative group p-2 hover:bg-white/5 rounded-full transition-colors">
              <Heart className={cn("w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors", favorites.length > 0 && "fill-red-500 text-red-500")} strokeWidth={2} />
              {favorites.length > 0 && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-black"></span>
              )}
            </Link>

            {/* User Avatar */}
            <div className="w-9 h-9 rounded-full bg-gray-800 border border-white/10 flex items-center justify-center cursor-pointer hover:border-white/30 transition-colors overflow-hidden relative">
              <User className="w-5 h-5 text-gray-400" />
            </div>

            {/* Mobile Search Icon (only visible on mobile, since pill search is hidden) */}
            <button
              className="md:hidden p-2 text-gray-300 hover:text-white"
              onClick={() => navigate('/search')}
            >
              <Search className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay removed - using Bottom Nav now */}
      </nav>
    </header>
  );
};

export default Navbar;
